import * as React from 'react';
import themePrimary from '../../styles/themePrimary';
import { Typography, makeStyles, useMediaQuery, Dialog, AppBar, IconButton, Toolbar, Slide } from '@material-ui/core';
import { UserStateContext } from '../../providers/UserStateContextProvider';
import { CheckOutStateContext } from '../../providers/CheckOutStateContextProvider';
import CloseIcon from '@material-ui/icons/Close';
import { TransitionProps } from '@material-ui/core/transitions';
import TextField, { TextFieldProps, TextFieldLabelPosition, TextFieldChangeHandler } from '../common/TextField';
import FullWidthButton, { FullWidthButtonProps, FullWidthButtonSize } from '../common/Buttons/FullWidthButton';
import ErrorDialog, { ErrorDialogProps } from '../common/ErrorDialog';
import { ElementDimensions } from '../../styles/ElementDimensions';
import { Skin } from '../../models/common/Skin';
import { String_PayCheck_RoomCharge_CompleteButton, String_PayCheck_RoomCharge_Instruction, String_PayCheck_RoomCharge_LastNameLabel, String_PayCheck_RoomCharge_RoomNumberLabel } from '../../utilities/StringHelper';
import PaymentTerms from './PaymentTerms';

const useStyles = makeStyles({
    appBar: {
        height: ElementDimensions.dialogBarHeight + 'px',
        position: 'relative',
        backgroundColor: props => (props as Skin).primary_main_color + ' !important',
    },
    closeButton: {
        height: '30px',
        width: '30px',
        color: themePrimary.palette.primary.contrastText,
    },
    details: {
        position: 'absolute',
        top: ElementDimensions.dialogBarHeight + 'px',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        overflowY: 'scroll',
        overflowX: 'hidden',
    },
    dialog: {
        backgroundColor: themePrimary.palette.secondary.light,
    },
    dialogLandscape: {
        backgroundColor: themePrimary.palette.secondary.light,
        left: 'calc(100vw *.3) !important',
        width: 'calc(100vw *.7)',
    },
    instructionText: {
        padding: ElementDimensions.padding20 + 'px',
        fontFamily: 'LeagueSpartan-Regular !important',
    },
    paymentTerms: {
        marginLeft: ElementDimensions.margin20 + 'px',
        marginRight: ElementDimensions.margin20 + 'px',
        marginBottom: ElementDimensions.margin20 + 'px',
    },
    textFieldGrid: {
        display: 'grid',
        gridTemplateColumns: '50% 50%',
        gridRowGap: '30px',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginBottom: '50px',
    },
    textFieldGridContainer: {
        marginLeft: ElementDimensions.margin20 + 'px',
        marginRight: ElementDimensions.margin20 + 'px',
    },
    textLabel: {
        textAlign: 'right',
        fontWeight: 'bold',
        marginTop: 'auto',
        marginBottom: 'auto',
        marginRight: ElementDimensions.margin10 + 'px',
    },
    title: {
        marginLeft: ElementDimensions.margin10 + 'px',
        flex: 1,
        color: themePrimary.palette.primary.contrastText,
    },
});

export type RoomChargeCompleteHandler = (lastName: string, roomNumber: string) => any;

export interface RoomChargeProps {
    onCompleteHandler: RoomChargeCompleteHandler;
}

const RoomCharge = (props: RoomChargeProps) => {
    const isPortrait = true;
    const { onCompleteHandler } = props;
    const [lastName, setLastName] = React.useState<string>('');
    const [roomNumber, setRoomNumber] = React.useState<string>('');
    const [errorDialogOpen, setErrorDialogOpen] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const userState = React.useContext(UserStateContext);
    const checkOutState = React.useContext(CheckOutStateContext);
    const classes = useStyles({ primary_main_color: checkOutState.state.skin.primary_main_color, 
        primary_light_color: checkOutState.state.skin.primary_light_color, primary_dark_color: checkOutState.state.skin.primary_dark_color });

    const handleNameChange: TextFieldChangeHandler = (newValue: string) => {
        setLastName(newValue.trim())
        onCompleteHandler(newValue.trim(), roomNumber);
    };

    const nameProps: TextFieldProps = {
        type: null,
        label: null,
        text: lastName,
        width: 150,
        disabled: false,
        labelPosition: TextFieldLabelPosition.Left,
        onChangeHandler: handleNameChange,
    }

    const handleRoomChange: TextFieldChangeHandler = (newValue: string) => {
        setRoomNumber(newValue.trim())
        onCompleteHandler(lastName, newValue.trim());
    };

    const roomProps: TextFieldProps = {
        type: null,
        label: null,
        text: roomNumber,
        width: 150,
        disabled: false,
        labelPosition: TextFieldLabelPosition.Left,
        onChangeHandler: handleRoomChange,
    }

    const handleErrorDialogClose = (event: React.MouseEvent<HTMLElement>) => {
        setErrorDialogOpen(false);
    }

    const errorDialogProps: ErrorDialogProps = {
        open: errorDialogOpen,
        message: errorMessage,
        tryAgain: false,
        onCloseHandler: handleErrorDialogClose,
    }

    return (
        <div>
            <div className={classes.paymentTerms}>
                <PaymentTerms />
            </div>       
            <div>
                <Typography variant='h6' className={classes.instructionText}>
                    {String_PayCheck_RoomCharge_Instruction.find(s => s.key === userState.state.locale)!!.value}
                </Typography>
            </div>
            <div className={classes.textFieldGridContainer}>
                <div className={classes.textFieldGrid}>
                    <Typography variant="body1" className={classes.textLabel}>
                        {String_PayCheck_RoomCharge_LastNameLabel.find(s => s.key === userState.state.locale)!!.value} 
                    </Typography>
                    <TextField 
                        type={nameProps.type}
                        label={nameProps.label}
                        text={nameProps.text}
                        width={nameProps.width}
                        disabled={nameProps.disabled}
                        labelPosition={nameProps.labelPosition}
                        onChangeHandler={nameProps.onChangeHandler} />
                    <Typography variant="body1" className={classes.textLabel}>
                        {String_PayCheck_RoomCharge_RoomNumberLabel.find(s => s.key === userState.state.locale)!!.value} 
                    </Typography>
                    <TextField 
                        type={roomProps.type}
                        label={roomProps.label}
                        text={roomProps.text}
                        width={roomProps.width}
                        disabled={roomProps.disabled}
                        labelPosition={roomProps.labelPosition}
                        onChangeHandler={roomProps.onChangeHandler} />
                </div>
            </div>

            <ErrorDialog 
                open={errorDialogProps.open}
                message={errorDialogProps.message}
                tryAgain={errorDialogProps.tryAgain}
                onCloseHandler={errorDialogProps.onCloseHandler} />
        </div>
    );
};

export default RoomCharge;